/**
 * Document Copy Modal — edit/add with name JA/EN/VI and instructions JA/EN/VI
 */
import React, { useState, useEffect } from 'react';
import { DocumentCopyItem } from './types';
import './DocumentCopyModal.css';

export type DocumentCopyModalMode = 'edit' | 'add';

export interface DocumentCopyModalSavePayload {
  nameJa: string;
  nameEn?: string;
  nameVi?: string;
  descriptionJa?: string;
  descriptionEn?: string;
  descriptionVi?: string;
}

export interface DocumentCopyModalProps {
  isOpen: boolean;
  mode: DocumentCopyModalMode;
  item?: DocumentCopyItem | null;
  onClose: () => void;
  onSave: (payload: DocumentCopyModalSavePayload) => void;
  onDelete?: (id: string) => void;
}

const DocumentCopyModal: React.FC<DocumentCopyModalProps> = ({
  isOpen,
  mode,
  item = null,
  onClose,
  onSave,
  onDelete,
}) => {
  const [nameJp, setNameJp] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [nameVi, setNameVi] = useState('');
  const [descriptionJa, setDescriptionJa] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');
  const [descriptionVi, setDescriptionVi] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && item) {
        setNameJp(item.nameJp);
        setNameEn(item.nameEn ?? '');
        setNameVi(item.nameVi ?? '');
        setDescriptionJa(item.descriptionJa ?? '');
        setDescriptionEn(item.descriptionEn ?? '');
        setDescriptionVi(item.descriptionVi ?? '');
      } else {
        setNameJp('');
        setNameEn('');
        setNameVi('');
        setDescriptionJa('');
        setDescriptionEn('');
        setDescriptionVi('');
      }
      setShowDeleteConfirm(false);
    }
  }, [isOpen, mode, item]);

  if (!isOpen) return null;

  const handleSave = () => {
    const trimmedName = nameJp.trim();
    if (!trimmedName) return;
    onSave({
      nameJa: trimmedName,
      nameEn: nameEn.trim() || undefined,
      nameVi: nameVi.trim() || undefined,
      descriptionJa: descriptionJa.trim() || undefined,
      descriptionEn: descriptionEn.trim() || undefined,
      descriptionVi: descriptionVi.trim() || undefined,
    });
    onClose();
  };

  const handleDelete = () => {
    if (mode === 'edit' && item && onDelete) {
      onDelete(item.id);
      onClose();
    }
    setShowDeleteConfirm(false);
  };

  const title = mode === 'edit' ? '書類の編集' : '書類の追加';

  return (
    <div className="doc-copy-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="doc-copy-modal doc-copy-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="doc-copy-modal__header">
          <h2 className="doc-copy-modal__title">{title}</h2>
          <button type="button" className="doc-copy-modal__close-btn" onClick={onClose} aria-label="閉じる">
            ×
          </button>
        </div>

        <div className="doc-copy-modal__body">
          <p className="doc-copy-modal__section-title">書類名</p>
          <div className="doc-copy-modal__field">
            <label className="doc-copy-modal__label">日本語（必須）</label>
            <input
              type="text"
              className="doc-copy-modal__input"
              value={nameJp}
              onChange={(e) => setNameJp(e.target.value)}
              placeholder="例: 住民票 (全部)"
            />
            <label className="doc-copy-modal__label">English</label>
            <input
              type="text"
              className="doc-copy-modal__input"
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
              placeholder="例: Resident record (all fields)"
            />
            <label className="doc-copy-modal__label">Tiếng Việt</label>
            <input
              type="text"
              className="doc-copy-modal__input"
              value={nameVi}
              onChange={(e) => setNameVi(e.target.value)}
              placeholder="例: Giấy chứng nhận cư trú"
            />
          </div>

          <p className="doc-copy-modal__section-title">説明・指示（従業員チャットに表示）</p>
          <div className="doc-copy-modal__field">
            <label className="doc-copy-modal__label">日本語</label>
            <textarea
              className="doc-copy-modal__textarea"
              value={descriptionJa}
              onChange={(e) => setDescriptionJa(e.target.value)}
              placeholder="提出者に表示する説明・指示（日本語）"
              rows={2}
            />
            <label className="doc-copy-modal__label">English</label>
            <textarea
              className="doc-copy-modal__textarea"
              value={descriptionEn}
              onChange={(e) => setDescriptionEn(e.target.value)}
              placeholder="Instructions (English)"
              rows={2}
            />
            <label className="doc-copy-modal__label">Tiếng Việt</label>
            <textarea
              className="doc-copy-modal__textarea"
              value={descriptionVi}
              onChange={(e) => setDescriptionVi(e.target.value)}
              placeholder="Hướng dẫn (Tiếng Việt)"
              rows={2}
            />
          </div>
        </div>

        <div className="doc-copy-modal__footer">
          {mode === 'edit' && onDelete && (
            <div className="doc-copy-modal__footer-left">
              {!showDeleteConfirm ? (
                <button type="button" className="doc-copy-modal__delete-btn" onClick={() => setShowDeleteConfirm(true)}>
                  この項目を削除
                </button>
              ) : (
                <div className="doc-copy-modal__delete-confirm">
                  <span className="doc-copy-modal__delete-confirm-text">削除しますか？</span>
                  <button type="button" className="doc-copy-modal__delete-confirm-yes" onClick={handleDelete}>
                    削除
                  </button>
                  <button type="button" className="doc-copy-modal__delete-confirm-no" onClick={() => setShowDeleteConfirm(false)}>
                    キャンセル
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="doc-copy-modal__footer-right">
            <button type="button" className="doc-copy-modal__cancel-btn" onClick={onClose}>
              キャンセル
            </button>
            <button type="button" className="doc-copy-modal__save-btn" onClick={handleSave} disabled={!nameJp.trim()}>
              {mode === 'edit' ? '保存' : '追加'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentCopyModal;
